import type { AttentionDisposition, AttentionLane, ClaimV2Item } from '../../../store';

export const LANE_COLORS: Record<AttentionLane, string> = {
  payer_rework: '#D97706',
  patient_billing: '#36229E',
  reversal: '#DC2626',
  manual_review: '#6B7280',
  cob_review: '#0D9488',
};

export const LANE_LABELS: Record<AttentionLane, string> = {
  payer_rework: 'Needs payer rework',
  patient_billing: 'Patient billing',
  reversal: 'Payment reversed',
  manual_review: 'Needs manual review',
  cob_review: 'Needs COB review',
};

export function getLaneColor(lane: AttentionLane | null | undefined): string {
  return LANE_COLORS[lane ?? 'manual_review'];
}

export function getLaneLabel(lane: AttentionLane | null | undefined): string {
  return LANE_LABELS[lane ?? 'manual_review'];
}

export type DispositionState = 'active' | 'dismissed' | 'snoozed';

/**
 * Current disposition state. 'active' means no disposition or snooze has expired.
 */
export function getDispositionState(
  disposition: AttentionDisposition | null | undefined,
): DispositionState {
  if (!disposition) return 'active';
  if (disposition.dismissed_at) return 'dismissed';
  if (disposition.snoozed_until && new Date(disposition.snoozed_until) > new Date()) {
    return 'snoozed';
  }
  return 'active';
}

/**
 * Resolves whether a claim is actively asking for attention right now, taking
 * the user's disposition (dismissed/snoozed) into account. A claim whose snooze
 * has already expired counts as active again.
 */
export function isActivelyNeedingAttention(claim: ClaimV2Item): boolean {
  if (!claim.era_triage?.needs_attention) return false;
  return getDispositionState(claim.attention_disposition) === 'active';
}

/**
 * Whether the Dismiss / "Remind me tomorrow" menu items should be offered.
 *
 * This is intentionally broader than `isActivelyNeedingAttention` — rejected
 * claims (277CA clearinghouse rejections) and draft-with-errors claims never
 * receive an `era_triage`, so they fail the `needs_attention` check, yet
 * billers still need to clear them from the morning worklist.
 *
 * Returns true when:
 *  1. The claim is actively needing attention (ERA-flagged path), OR
 *  2. The claim is in the review phase without ERA triage (e.g. 277CA
 *     clearinghouse rejections) or has draft errors, AND has not already
 *     been dismissed or snoozed.
 */
export function canDispositionClaim(claim: ClaimV2Item): boolean {
  if (isActivelyNeedingAttention(claim)) return true;

  const isReviewWithoutTriage = claim.phase === 'review' && !claim.era_triage?.needs_attention;
  const isDraftWithErrors = claim.phase === 'drafts' && (claim.error_count ?? 0) > 0;

  if (!isReviewWithoutTriage && !isDraftWithErrors) return false;

  return getDispositionState(claim.attention_disposition) === 'active';
}
